import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Hand,
  LinkIcon,
  Mail,
  MessageSquareMore,
  QrCode,
  Send,
  SquareDashedBottomCode,
  Store,
  Webhook,
} from "lucide-react"
import { BigBlueIcon } from "modules/shared/icons/bigblue"
import { WhatsappIcon } from "modules/shared/icons/whatsapp"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignTriggerTypeEnum } from "schema"

type Props = {
  trigger: keyof typeof CampaignTriggerTypeEnum | null | undefined
  emptyValue?: string
}

export const CampaignTriggerIcon = ({ trigger, emptyValue }: Props) => {
  const { t } = useTranslation()

  const triggerCopy = useMemo(() => {
    if (!trigger) return null

    if (trigger === CampaignTriggerTypeEnum.QR) return t("campaigns.configuration.tabs.trigger.qr")
    if (trigger === CampaignTriggerTypeEnum.InstagramDirectMessage)
      return t("campaigns.configuration.tabs.trigger.instagramDirectMessage")
    if (trigger === CampaignTriggerTypeEnum.InstagramCommentReply)
      return t("campaigns.configuration.tabs.trigger.instagramCommentReply")
    if (trigger === CampaignTriggerTypeEnum.MessengerDirectMessage)
      return t("campaigns.configuration.tabs.trigger.messengerDirectMessage")
    if (trigger === CampaignTriggerTypeEnum.MessengerCommentReply)
      return t("campaigns.configuration.tabs.trigger.messengerCommentReply")
    if (trigger === CampaignTriggerTypeEnum.Link)
      return t("campaigns.configuration.tabs.trigger.link")
    if (trigger === CampaignTriggerTypeEnum.Widget)
      return t("campaigns.configuration.tabs.trigger.widget")
    if (trigger === CampaignTriggerTypeEnum.Webhook)
      return t("campaigns.configuration.tabs.trigger.webhook")
    if (trigger === CampaignTriggerTypeEnum.Shopify)
      return t("campaigns.configuration.tabs.trigger.shopify")
    if (trigger === CampaignTriggerTypeEnum.BigBlue)
      return t("campaigns.configuration.tabs.trigger.bigblue")
    if (trigger === CampaignTriggerTypeEnum.Manual)
      return t("campaigns.configuration.tabs.trigger.manual")
    return null
  }, [t, trigger])

  const Icon = useMemo(() => {
    if (!trigger) return null

    switch (trigger) {
      case CampaignTriggerTypeEnum.Webhook:
        return Webhook
      case CampaignTriggerTypeEnum.QR:
        return QrCode
      case CampaignTriggerTypeEnum.Widget:
        return SquareDashedBottomCode
      case CampaignTriggerTypeEnum.Link:
        return LinkIcon
      case CampaignTriggerTypeEnum.InstagramDirectMessage:
      case CampaignTriggerTypeEnum.MessengerDirectMessage:
        return Send
      case CampaignTriggerTypeEnum.InstagramCommentReply:
      case CampaignTriggerTypeEnum.MessengerCommentReply:
        return MessageSquareMore
      case CampaignTriggerTypeEnum.WhatsappDefault:
        return WhatsappIcon
      case CampaignTriggerTypeEnum.EmailInbox:
        return Mail
      case CampaignTriggerTypeEnum.Shopify:
        return Store
      case CampaignTriggerTypeEnum.BigBlue:
        return BigBlueIcon
      case CampaignTriggerTypeEnum.Manual:
        return Hand
      default:
        return null
    }
  }, [trigger])

  if (!Icon) return emptyValue

  const iconElement = (
    <span className="inline-flex">
      <Icon className="size-4.5" />
    </span>
  )

  if (!triggerCopy) return iconElement

  return (
    <Tooltip>
      <TooltipTrigger asChild>{iconElement}</TooltipTrigger>
      <TooltipContent>{triggerCopy}</TooltipContent>
    </Tooltip>
  )
}
